import React, { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { MessageCircle, Phone, Receipt, Users, Star } from "lucide-react";

function waLink(phone, text) {
  // normalize phone: strip non-digits, keep leading country code
  let p = (phone || "").replace(/[^\d]/g, "");
  if (p.startsWith("0")) p = "62" + p.slice(1); // ID default
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [promos, setPromos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, s, p] = await Promise.all([
        api.get("/customers"),
        api.get("/settings"),
        api.get("/promos"),
      ]);
      setCustomers(c.data);
      setSettings(s.data);
      setPromos(p.data);
    })();
  }, []);

  const openCustomer = async (c) => {
    setSelected(c);
    const { data } = await api.get(`/customers/${encodeURIComponent(c.phone)}/orders`);
    setOrders(data);
  };

  const activePromo = promos.find((p) => p.active);

  const sendPromoWA = (c) => {
    const name = c.name || "pelanggan setia";
    const promoText = activePromo
      ? `pakai kode *${activePromo.code}* untuk ${activePromo.type === "percent" ? `diskon ${activePromo.value}%` : `potongan ${formatRupiah(activePromo.value)}`}`
      : `kunjungi kami lagi!`;
    const msg = `Halo ${name} 👋\n\nTerima kasih sudah mampir ke ${settings?.restaurant_name || "warung kami"}.\nSebagai apresiasi, ${promoText}.\n\nSampai jumpa lagi!`;
    window.open(waLink(c.phone, msg), "_blank");
  };

  const sendReceiptWA = (c, o) => {
    const lines = [];
    lines.push(`🧾 *Struk — ${settings?.restaurant_name || "Warung"}*`);
    lines.push(`No. ${o.order_no}`);
    lines.push(`Meja: ${o.table_number}`);
    lines.push("");
    for (const it of o.items) {
      const opt = (it.options || []).reduce((a, x) => a + (x.price_delta || 0), 0);
      lines.push(`${it.quantity}× ${it.name} — ${formatRupiah((it.unit_price + opt) * it.quantity)}`);
    }
    lines.push("");
    if (o.subtotal) lines.push(`Subtotal: ${formatRupiah(o.subtotal)}`);
    if (o.discount) lines.push(`Diskon (${o.promo?.code || "promo"}): -${formatRupiah(o.discount)}`);
    lines.push(`*Total: ${formatRupiah(o.total)}*`);
    lines.push("");
    lines.push("Terima kasih! 🙏");
    window.open(waLink(c.phone, lines.join("\n")), "_blank");
  };

  return (
    <OwnerLayout>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Riwayat Pelanggan</h1>
        <p className="text-[#5C6A61] mt-1">Pelanggan yang sudah pernah membayar — kirim struk via WhatsApp atau tawarkan promo</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-md border border-[#D3CEC4] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EAE7DF] flex items-center justify-between">
            <div className="font-display font-semibold text-[#1E3A2B] flex items-center gap-2"><Users className="w-4 h-4" /> {customers.length} Pelanggan</div>
          </div>
          <div className="divide-y divide-[#EAE7DF]" data-testid="customers-list">
            {customers.length === 0 && (
              <div className="p-10 text-center text-[#5C6A61]">Belum ada pelanggan. Mintalah nomor WA saat checkout.</div>
            )}
            {customers.map((c) => (
              <button
                key={c.phone}
                data-testid={`customer-${c.phone}`}
                onClick={() => openCustomer(c)}
                className={`w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[#F9F9F6] ${selected?.phone === c.phone ? "bg-[#F9F9F6]" : ""}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-[#1E3A2B] truncate">{c.name || "Pelanggan"}</div>
                  <div className="text-xs text-[#5C6A61] flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display font-semibold text-[#1E3A2B]">{formatRupiah(c.total_spent)}</div>
                  <div className="text-xs text-[#5C6A61]">{c.orders} pesanan</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-md border border-[#D3CEC4] p-5" data-testid="customer-detail">
          {!selected ? (
            <div className="text-center py-10 text-[#5C6A61]">Pilih pelanggan untuk melihat riwayat</div>
          ) : (
            <>
              <div className="mb-4">
                <div className="font-display text-lg font-semibold text-[#1E3A2B]">{selected.name || "Pelanggan"}</div>
                <div className="text-sm text-[#5C6A61]">{selected.phone}</div>
                <div className="text-xs text-[#5C6A61] mt-1">Total belanja: <span className="font-medium text-[#1E3A2B]">{formatRupiah(selected.total_spent)}</span></div>
              </div>
              <button
                data-testid="btn-send-promo-wa"
                onClick={() => sendPromoWA(selected)}
                className="w-full bg-[#25D366] text-white hover:opacity-90 px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2 mb-4"
              >
                <MessageCircle className="w-4 h-4" /> {activePromo ? `Kirim Promo ${activePromo.code}` : "Kirim Terima Kasih via WA"}
              </button>
              <div className="text-xs uppercase tracking-widest font-bold text-[#5C6A61] mb-2">Riwayat Pesanan</div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {orders.map((o) => (
                  <div key={o.id} className="border border-[#EAE7DF] rounded-md p-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium text-[#1E3A2B]">#{o.order_no}</div>
                      <div className="font-display font-semibold text-[#C25934]">{formatRupiah(o.total)}</div>
                    </div>
                    <div className="text-xs text-[#5C6A61]">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                    {o.rating ? (
                      <div className="flex items-center gap-1 mt-1">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-3 h-3 ${o.rating >= n ? "fill-[#D4A373] text-[#D4A373]" : "text-[#D3CEC4]"}`} />
                        ))}
                        {o.rating_comment && <span className="text-xs text-[#5C6A61] italic ml-1">"{o.rating_comment}"</span>}
                      </div>
                    ) : null}
                    <button
                      data-testid={`btn-send-receipt-${o.id}`}
                      onClick={() => sendReceiptWA(selected, o)}
                      className="mt-2 text-xs text-[#25D366] font-medium inline-flex items-center gap-1 hover:underline"
                    >
                      <Receipt className="w-3 h-3" /> Kirim struk via WhatsApp
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
}
